// lib/licensing/license-context.tsx
//
// Expone la licencia on-prem YA VERIFICADA (firma ECDSA validada en el gate)
// al resto de la app, para que el gating de módulos (`can()`) y el límite de
// asientos respeten el tier que concede el token.
//
// El default es `{ payload: null }`, que significa "sin restricción de tier":
//   - Ediciones cloud (SaaS/Desktop): el LicenseGate es transparente y NO monta
//     este provider, así que `useLicense()` devuelve el default → todo permitido
//     (el control real es la suscripción del lado servidor).
//   - On-prem: el LicenseGate monta el provider con el payload verificado.
//
// Reglas de tier derivadas del payload:
//   - `mods` vacío  => todos los módulos de la edición (sin restricción).
//   - `mods` no vacío => solo esos módulos (+ núcleo/infra, ver usePermissions).
//   - `seats` 0     => usuarios ilimitados.
//
// NUEVO: `payload.mods` se persiste y se firma como MODULE IDS PUROS (no
// tablas). El gating por tabla se computa en runtime expandiendo con
// `resolveEffectivePermissions` (tablas propias + readOnly de cada módulo).
// Mantenemos defensa contra tokens legacy mezclados filtrando con
// `moduleIdsOnly` antes de resolver.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { LicensePayload } from "@/convex/system/licensing/crypto";
import { ALWAYS_ENABLED_MODULES } from "@/convex/system/permissions/constants";
import {
  moduleIdsOnly,
  resolveEffectivePermissions,
} from "@/components/licenses/module-helpers";

export type LicenseContextValue = {
  /** Payload verificado de la licencia on-prem, o null si no aplica (cloud). */
  payload: LicensePayload | null;
  /**
   * Módulos permitidos por la licencia. `null` = sin restricción (cloud o
   * `mods` vacío). Un Set para lookup O(1).
   */
  allowedModules: ReadonlySet<string> | null;
  /**
   * Tablas permitidas por la licencia (propias + readOnly de los módulos
   * concedidos). `null` = sin restricción. Útil cuando el gating pregunta
   * por tabla en lugar de por módulo (poco común pero ocurre).
   */
  allowedTables: ReadonlySet<string> | null;
  /** Límite de asientos. `null` = sin límite (cloud o `seats` 0). */
  seats: number | null;
  /**
   * ¿El módulo está permitido por el tier de la licencia? Siempre true en
   * cloud o cuando `mods` está vacío. Los módulos núcleo/infra
   * (ALWAYS_ENABLED) siempre pasan. Útil para distinguir un bloqueo por tier
   * de uno por permisos de rol.
   */
  isModuleAllowedByTier: (module: string) => boolean;
};

const DEFAULT: LicenseContextValue = {
  payload: null,
  allowedModules: null,
  allowedTables: null,
  seats: null,
  isModuleAllowedByTier: () => true,
};

const LicenseContext = createContext<LicenseContextValue>(DEFAULT);

export function useLicense(): LicenseContextValue {
  return useContext(LicenseContext);
}

export function LicenseProvider({
  payload,
  children,
}: {
  payload: LicensePayload | null;
  children: ReactNode;
}) {
  const value = useMemo<LicenseContextValue>(() => {
    if (!payload) return DEFAULT;
    // Defensa: si el token viene con un payload legacy (mezcla de ids +
    // tablas), filtramos a module ids puros antes de resolver permisos.
    const pureModuleIds = moduleIdsOnly(payload.mods);
    const resolved =
      pureModuleIds.length > 0
        ? resolveEffectivePermissions(pureModuleIds, payload.ed)
        : null;
    const allowedModules = resolved?.modules ?? null;
    const allowedTables = resolved?.tables ?? null;
    return {
      payload,
      allowedModules,
      allowedTables,
      seats: payload.seats > 0 ? payload.seats : null,
      isModuleAllowedByTier: (module: string) =>
        !allowedModules ||
        allowedModules.has(module) ||
        allowedTables?.has(module) ||
        ALWAYS_ENABLED_MODULES.includes(module),
    };
  }, [payload]);

  return (
    <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
  );
}
